import { Component, computed, forwardRef, input, signal, ChangeDetectionStrategy } from '@angular/core';

import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { BaseControlDirective } from '../control-value-accessor.directive';
import { FormInputAutocomplete, FormInputCapitalize, FormInputType } from '../../../types/form.types';
import { FormFieldComponent } from '../form-field/form-field.component';
import { FormFieldEventsDirective } from '../form-field-events.directive';
import { AriaLabelledByDirective } from '../aria-labelledby.directive';
import { IconComponent } from '../../icon/icon.component';
import { IconSet } from '../../../types/icon.types';

@Component({
  selector: 'ds-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  imports: [ReactiveFormsModule, FormFieldComponent, AriaLabelledByDirective, IconComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  hostDirectives: [
    {
      directive: FormFieldEventsDirective,
      outputs: ['inputEvent', 'blurEvent'],
    },
  ],
})
export class InputComponent<Tvalue> extends BaseControlDirective<Tvalue> {
  public type = input<FormInputType>('text');
  public value = input<string | number>();
  public placeholder = input<string>('');
  public readonly = input<boolean>();
  public minlength = input<string | number | null>(null);
  public maxlength = input<string | number | null>(null);
  public min = input<number | string | undefined>();
  public max = input<number | string | undefined>();
  public pattern = input<string | RegExp>();
  public autocomplete = input<string>();
  public autocapitalize = input<FormInputCapitalize>();
  public icon = input<string>();
  public iconSet = input<IconSet>('basic');
  public passwordToggle = input<boolean>(false);
  public passwordVisible = signal(false);
  public hintAfter = input<string>(); // pass to form-field

  public togglePasswordVisibility() {
    this.passwordVisible.update((v) => !v);
  }

  public describedByIds = computed(() => {
    const ids: string[] = [];
    if (this.hintAfter()) {
      ids.push(`hintid-${this.controlId()}`);
    }

    if (this.hasError()) {
      ids.push(`errorid-${this.controlId()}`);
    }

    return ids.length > 0 ? ids.join(' ') : null;
  });

  public inputType = computed(() => {
    if (this.type() === 'password' && this.passwordToggle()) {
      return this.passwordVisible() ? 'text' : 'password';
    }

    return this.type();
  });

  public inputmode = computed(() => {
    switch (this.type()) {
      case 'email':
        return 'email';
      case 'number':
        return 'numeric';
      case 'tel':
        return 'tel';
      default:
        return 'text';
    }
  });

  protected setValue(event: Event) {
    const target = event.target as HTMLInputElement;
    const value = target?.value;
    this.control.setValue(value, { emitEvent: true });
  }
}
